/**
 * Campaign Wizard
 * 4-step guided wizard: Name & Goal -> Configure -> Review -> Launch
 * Modeled after the Generate My Month wizard UI patterns.
 */
import { client } from '../api/client.js';

var PLATFORMS = [
  { id: 'facebook', label: 'Facebook', color: '#1877F2', short: 'FB' },
  { id: 'instagram', label: 'Instagram', color: '#E4405F', short: 'IG' },
  { id: 'x', label: 'X (Twitter)', color: '#94A3B8', short: 'X' },
  { id: 'linkedin', label: 'LinkedIn', color: '#0A66C2', short: 'LI' },
];

var DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
var TONES = ['Professional','Casual','Witty','Bold','Inspirational','Educational','Provocative','Storytelling','Other'];

export class CampaignWizard {
  constructor() {
    this.step = 1;
    this.name = '';
    this.goal = '';
    this.context = '';
    this.selectedPlatforms = ['facebook', 'instagram', 'x', 'linkedin'];
    this.selectedDays = ['Mon', 'Wed', 'Fri'];
    this.postingTime = '10:00';
    this.totalItems = 12;
    this.durationDays = 14;
    this.tone = 'Professional';
    this.customTone = '';
    this.startDate = '';
    this.generating = false;
    this.generationError = null;
    this.campaignResult = null; // { id, items }
    this.expandedPostIndex = -1;
    this.overlay = null;
    this.onComplete = null; // callback after launch
    this.brainData = null; // { connected, profile, counts }
    // Phase 37 Commit 8 — faceless video derivative toggle. (The old
    // generateAiImages/generateAiVideos checkboxes were removed: the server
    // create route never read those flags, so they were dead controls.)
    this.generateFacelessVideo = false;
  }

  get estimatedPosts() {
    // Mirror the server's slot counter exactly (services/campaigns.js
    // generateSlots): walk each calendar day from the start date through
    // start + durationDays INCLUSIVE, counting days whose weekday is
    // selected, × platforms. The old days×platforms×weeks approximation
    // drifted from what the server actually created (e.g. promised 24,
    // created 28) and let over-cap configs through to a server rejection.
    var dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var start = this.startDate ? new Date(this.startDate) : new Date();
    if (isNaN(start.getTime())) start = new Date();
    var end = new Date(start.getTime() + this.durationDays * 86400000);
    var matchingDays = 0;
    var current = new Date(start);
    while (current <= end) {
      if (this.selectedDays.indexOf(dayNames[current.getDay()]) !== -1) matchingDays++;
      current.setDate(current.getDate() + 1);
    }
    return matchingDays * this.selectedPlatforms.length;
  }

  async open(opts) {
    this.step = 1;
    this.name = '';
    this.goal = '';
    this.context = '';
    this.generating = false;
    this.generationError = null;
    this.campaignResult = null;
    this.expandedPostIndex = -1;
    this.tone = 'Professional';
    this.customTone = '';
    this.startDate = '';
    this.onComplete = (opts && opts.onComplete) || null;

    // Apply template pre-fill if provided
    if (opts && opts.template) {
      this.name = opts.template.name || '';
      this.goal = opts.template.goal || '';
      this.context = opts.template.context || '';
      if (opts.template.totalItems) this.totalItems = opts.template.totalItems;
      if (opts.template.durationDays) this.durationDays = opts.template.durationDays;
      if (opts.template.selectedDays) this.selectedDays = opts.template.selectedDays;
    }

    // Try to load connected platforms — same /api/integrations payload the
    // Settings page uses ({ items: [{ provider, status }] }).
    try {
      var integ = await client.getIntegrations();
      var items = (integ && integ.items) || [];
      var connected = [];
      for (var ci = 0; ci < items.length; ci++) {
        if (!items[ci] || items[ci].status !== 'connected') continue;
        if (items[ci].provider === 'meta') connected.push('facebook', 'instagram');
        else if (items[ci].provider === 'x') connected.push('x');
        else if (items[ci].provider === 'linkedin') connected.push('linkedin');
      }
      if (connected.length > 0) this.selectedPlatforms = connected;
    } catch (e) { /* use defaults */ }

    // Load brain summary
    try {
      this.brainData = await client.getBrainSummary();
    } catch (e) { this.brainData = null; }

    this.overlay = document.createElement('div');
    this.overlay.className = 'cwiz-overlay';
    this.overlay.setAttribute('data-testid', 'campaign-wizard-overlay');
    this.render();
    document.body.appendChild(this.overlay);
    this.bindEvents();
    // Escape closes the wizard (standard modal affordance — there was no
    // keyboard exit before). Guarded the same way as close().
    var self = this;
    this._onEscape = function(e) {
      if (e.key === 'Escape') self.close();
    };
    document.addEventListener('keydown', this._onEscape);
  }

  close() {
    // Don't tear down mid-generation: the server request is already running
    // and would complete invisibly, so a user who closes and retries ends up
    // with duplicate campaigns.
    if (this.generating) return;
    var hadResult = !!this.campaignResult;
    if (this._onEscape) {
      document.removeEventListener('keydown', this._onEscape);
      this._onEscape = null;
    }
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
    // If a campaign was created, force refresh the campaigns page
    if (hadResult && window.location.hash === '#campaigns') {
      window.location.hash = '#home';
      setTimeout(function() { window.location.hash = '#campaigns'; }, 50);
    }
  }

  render() {
    if (!this.overlay) return;

    var stepLabels = ['Campaign', 'Schedule', 'Review', 'Launch'];
    var stepsHtml = '';
    for (var i = 0; i < 4; i++) {
      var cls = '';
      if (i + 1 === this.step) cls = ' active';
      else if (i + 1 < this.step) cls = ' done';
      var clickable = (i + 1 < this.step) && !this.generating;
      stepsHtml += '<div class="cwiz-step-dot' + cls + (clickable ? ' clickable' : '') + '"'
        + (clickable ? ' data-cwiz-step="' + (i + 1) + '"' : '')
        + '>' + (i + 1 < this.step ? '\u2713' : (i + 1)) + '</div>';
      if (i < 3) {
        stepsHtml += '<div class="cwiz-step-line' + (i + 1 < this.step ? ' done' : '') + '"></div>';
      }
    }

    var stepLabelHtml = '';
    for (var j = 0; j < 4; j++) {
      var labelCls = j + 1 === this.step ? ' active' : '';
      stepLabelHtml += '<span class="cwiz-step-label' + labelCls + '">' + stepLabels[j] + '</span>';
    }

    var bodyHtml = '';
    if (this.step === 1) bodyHtml = this.renderStep1();
    else if (this.step === 2) bodyHtml = this.renderStep2();
    else if (this.step === 3) bodyHtml = this.renderStep3();
    else if (this.step === 4) bodyHtml = this.renderStep4();

    this.overlay.innerHTML = '<div class="cwiz-container">'
      + '<div class="cwiz-header">'
      +   '<div class="cwiz-header-top">'
      +     '<div class="cwiz-title">New Campaign</div>'
      +     '<button class="cwiz-close" data-cwiz="close">&times;</button>'
      +   '</div>'
      +   '<div class="cwiz-steps">' + stepsHtml + '</div>'
      +   '<div class="cwiz-step-labels">' + stepLabelHtml + '</div>'
      + '</div>'
      + '<div class="cwiz-body">' + bodyHtml + '</div>'
      + '</div>';

    this.bindEvents();
  }

  renderBrainPanel() {
    var b = this.brainData;
    if (!b) return '';
    if (!b.connected) {
      return '<div class="wiz-brain-panel wiz-brain-empty">'
        + '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z"/><line x1="9" y1="21" x2="15" y2="21"/></svg>'
        + '<span>No brand details yet &mdash; <a href="#brain" class="wiz-brain-link" data-cwiz="close">add your brand voice</a> so posts sound like you</span>'
        + '</div>';
    }
    var details = [];
    if (b.profile.voice) details.push('<span class="wiz-brain-tag">Voice: ' + b.profile.voice.substring(0, 40) + '</span>');
    if (b.profile.audience) details.push('<span class="wiz-brain-tag">Audience: ' + b.profile.audience.substring(0, 40) + '</span>');
    if (b.counts.facts > 0) details.push('<span class="wiz-brain-tag">' + b.counts.facts + ' saved note' + (b.counts.facts !== 1 ? 's' : '') + '</span>');
    if (b.counts.documents > 0) details.push('<span class="wiz-brain-tag">' + b.counts.documents + ' document' + (b.counts.documents !== 1 ? 's' : '') + '</span>');
    if (b.counts.personas > 0) details.push('<span class="wiz-brain-tag">' + b.counts.personas + ' audience persona' + (b.counts.personas !== 1 ? 's' : '') + '</span>');
    if (b.counts.highPerformers > 0) details.push('<span class="wiz-brain-tag wiz-brain-tag-perf">' + b.counts.highPerformers + ' post' + (b.counts.highPerformers !== 1 ? 's' : '') + ' that did well before</span>');
    var detailsHtml = '';
    for (var i = 0; i < details.length; i++) { detailsHtml += details[i]; }
    return '<div class="wiz-brain-panel wiz-brain-active">'
      + '<div class="wiz-brain-header">'
      +   '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--success,#22c55e)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a7 7 0 0 0-7 7c0 2.38 1.19 4.47 3 5.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26c1.81-1.27 3-3.36 3-5.74a7 7 0 0 0-7-7z"/><line x1="9" y1="21" x2="15" y2="21"/></svg>'
      +   '<span class="wiz-brain-status">Brand profile connected</span>'
      + '</div>'
      + '<div class="wiz-brain-details">' + detailsHtml + '</div>'
      + '</div>';
  }

  // ── Step 1: Campaign Name & Goal ──────────────────────────────────────────

  renderStep1() {
    return '<div class="cwiz-step-content">'
      + '<div class="cwiz-hero-icon"><svg width="44" height="44" viewBox="0 0 48 48" fill="none"><circle cx="24" cy="24" r="18" fill="rgba(139,92,246,0.1)"/><path d="M14 30 L24 10 L34 30 Z" stroke="#a78bfa" stroke-width="2.5" stroke-linejoin="round" fill="none"/><circle cx="24" cy="24" r="3" stroke="#c4b5fd" stroke-width="1.5" opacity="0.6"/></svg></div>'
      + '<h2 class="cwiz-heading">What\'s this campaign about?</h2>'
      + '<p class="cwiz-subtext">Give your campaign a name and tell us the goal. We\'ll generate a full content schedule around it.</p>'
      + this.renderBrainPanel()
      + '<div class="cwiz-field">'
      +   '<label class="cwiz-label">Campaign Name <span style="color:var(--primary)">*</span></label>'
      +   '<input type="text" class="cwiz-input" id="cwiz-name" data-testid="campaign-name" placeholder="e.g., Summer Product Launch" value="' + this.escapeAttr(this.name) + '" />'
      + '</div>'
      + '<div class="cwiz-field">'
      +   '<label class="cwiz-label">Goal / Topic <span style="color:var(--primary)">*</span></label>'
      +   '<textarea class="cwiz-textarea" id="cwiz-goal" data-testid="campaign-goal" rows="3" placeholder="What are we trying to achieve? e.g., Drive signups for the new feature launch...">' + this.escapeHtml(this.goal) + '</textarea>'
      + '</div>'
      + '<div class="cwiz-field">'
      +   '<label class="cwiz-label">Additional context <span class="cwiz-optional">(optional)</span></label>'
      +   '<textarea class="cwiz-textarea cwiz-textarea-sm" id="cwiz-context" data-testid="campaign-context" rows="2" placeholder="Key dates, products, target audience...">' + this.escapeHtml(this.context) + '</textarea>'
      + '</div>'
      + '<div class="cwiz-footer">'
      +   '<div></div>'
      +   '<button class="cwiz-btn-primary" data-cwiz="next" data-testid="campaign-wizard-next" ' + (!this.name.trim() || !this.goal.trim() ? 'disabled' : '') + '>Next \u2192</button>'
      + '</div>'
      + '</div>';
  }

  // ── Step 2: Schedule Configuration ────────────────────────────────────────

  renderStep2() {
    var platformChips = '';
    for (var i = 0; i < PLATFORMS.length; i++) {
      var p = PLATFORMS[i];
      var selected = this.selectedPlatforms.indexOf(p.id) !== -1;
      platformChips += '<button class="cwiz-chip' + (selected ? ' selected' : '') + '" data-cwiz-platform="' + p.id + '" data-testid="campaign-platform-' + p.id + '">'
        + '<span class="cwiz-chip-dot" style="background:' + p.color + '"></span>' + p.label
        + '</button>';
    }

    var dayButtons = '';
    for (var d = 0; d < DAYS_OF_WEEK.length; d++) {
      var day = DAYS_OF_WEEK[d];
      var dayActive = this.selectedDays.indexOf(day) !== -1;
      dayButtons += '<button class="cwiz-day-btn' + (dayActive ? ' active' : '') + '" data-cwiz-day="' + day + '">' + day + '</button>';
    }

    var durationButtons = '';
    var durations = [7, 14, 21, 30];
    var durationLabels = ['1 week', '2 weeks', '3 weeks', '1 month'];
    for (var dl = 0; dl < durations.length; dl++) {
      durationButtons += '<button class="cwiz-dur-btn' + (this.durationDays === durations[dl] ? ' active' : '') + '" data-cwiz-dur="' + durations[dl] + '">' + durationLabels[dl] + '</button>';
    }

    var estimated = this.estimatedPosts;
    var capped = estimated > 50;
    var displayCount = capped ? 50 : estimated;

    return '<div class="cwiz-step-content">'
      + '<h2 class="cwiz-heading">Set your posting schedule</h2>'
      + '<p class="cwiz-subtext">Choose where and when to post. We\'ll write a post for every slot on the schedule.</p>'
      + '<div class="cwiz-field">'
      +   '<label class="cwiz-label">Platforms</label>'
      +   '<div class="cwiz-chips">' + platformChips + '</div>'
      + '</div>'
      + '<div class="cwiz-field">'
      +   '<label class="cwiz-label">Posting days</label>'
      +   '<div class="cwiz-day-group">' + dayButtons + '</div>'
      + '</div>'
      + '<div class="cwiz-row">'
      +   '<div class="cwiz-field cwiz-field-half">'
      +     '<label class="cwiz-label">Posting time</label>'
      +     '<input type="time" class="cwiz-input cwiz-time-input" id="cwiz-time" value="' + this.postingTime + '" />'
      +   '</div>'
      +   '<div class="cwiz-field cwiz-field-half">'
      +     '<label class="cwiz-label">Duration</label>'
      +     '<div class="cwiz-dur-group">' + durationButtons + '</div>'
      +   '</div>'
      + '</div>'
      + '<div class="cwiz-row">'
      +   '<div class="cwiz-field cwiz-field-half">'
      +     '<label class="cwiz-label">Tone</label>'
      +     '<select class="cwiz-input" id="cwiz-tone">' + this.buildToneOptions() + '</select>'
      +     (this.tone === 'Other' ? '<input type="text" class="cwiz-input" id="cwiz-tone-custom" placeholder="Type your tone..." value="' + this.escapeAttr(this.customTone) + '" style="margin-top:8px" />' : '')
      +   '</div>'
      +   '<div class="cwiz-field cwiz-field-half">'
      +     '<label class="cwiz-label" for="cwiz-start-date" style="cursor:pointer">Start date &amp; time <span class="cwiz-optional">(optional)</span></label>'
      +     '<input type="datetime-local" class="cwiz-input cwiz-date-input" id="cwiz-start-date" value="' + this.startDate + '" />'
      +   '</div>'
      + '</div>'
      + '<div class="cwiz-field gmw-ai-media-section">'
      +   '<label class="cwiz-label">AI-Generated Media</label>'
      +   '<div class="gmw-ai-media-toggles">'
      +     '<label class="gmw-ai-toggle-row" data-testid="cwiz-faceless-video-row">'
      +       '<input type="checkbox" class="gmw-ai-checkbox" data-cwiz-ai="faceless" data-testid="cwiz-faceless-check"' + (this.generateFacelessVideo ? ' checked' : '') + '>'
      +       '<span class="gmw-ai-toggle-label">'
      +         '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>'
      +         ' Add short videos'
      +         '<span class="gmw-ai-toggle-hint"> — a narrated slideshow video (no camera needed) for each platform that supports video, about $0.12 per video. Paid plans only.</span>'
      +       '</span>'
      +     '</label>'
      +   '</div>'
      + '</div>'
      + '<div class="cwiz-calc">'
      +   '<div class="cwiz-calc-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--primary,#8B5CF6)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg></div>'
      +   '<div class="cwiz-calc-text">' + (capped
            ? 'That\'s <strong>' + estimated + ' posts</strong> \u2014 over the <strong>50-post</strong> limit for one campaign. Shorten the duration or pick fewer days/platforms to continue.'
            : 'This will generate <strong>' + displayCount + ' posts</strong> across <strong>' + this.selectedPlatforms.length + ' platform' + (this.selectedPlatforms.length !== 1 ? 's' : '') + '</strong> over <strong>' + this.durationDays + ' days</strong>')
      +   '</div>'
      + '</div>'
      + '<div class="cwiz-footer">'
      +   '<button class="cwiz-btn-ghost" data-cwiz="back">\u2190 Back</button>'
      +   '<button class="cwiz-btn-primary" data-cwiz="next" data-testid="campaign-wizard-next" ' + (this.selectedPlatforms.length === 0 || this.selectedDays.length === 0 || capped ? 'disabled' : '') + '>Review \u2192</button>'
      + '</div>'
      + '</div>';
  }

  // ── Step 3: Review Before Launch ──────────────────────────────────────────

  renderStep3() {
    var estimated = this.estimatedPosts;
    var capped = estimated > 50;
    var displayCount = capped ? 50 : estimated;

    var platList = '';
    for (var i = 0; i < this.selectedPlatforms.length; i++) {
      var platInfo = null;
      for (var pi = 0; pi < PLATFORMS.length; pi++) {
        if (PLATFORMS[pi].id === this.selectedPlatforms[i]) { platInfo = PLATFORMS[pi]; break; }
      }
      if (platInfo) {
        platList += '<span class="cwiz-review-plat"><span class="cwiz-chip-dot" style="background:' + platInfo.color + '"></span>' + platInfo.label + '</span>';
      }
    }

    var daysList = this.selectedDays.join(', ');

    return '<div class="cwiz-step-content">'
      + '<h2 class="cwiz-heading">Review your campaign</h2>'
      + '<p class="cwiz-subtext">Confirm everything looks good before we generate content.</p>'
      + '<div class="cwiz-review-card">'
      +   '<div class="cwiz-review-row">'
      +     '<span class="cwiz-review-label">Campaign</span>'
      +     '<span class="cwiz-review-value">' + this.escapeHtml(this.name) + '</span>'
      +   '</div>'
      +   '<div class="cwiz-review-row">'
      +     '<span class="cwiz-review-label">Goal</span>'
      +     '<span class="cwiz-review-value cwiz-review-goal">' + this.escapeHtml(this.goal) + '</span>'
      +   '</div>'
      + (this.context.trim() ? '<div class="cwiz-review-row"><span class="cwiz-review-label">Context</span><span class="cwiz-review-value cwiz-review-goal">' + this.escapeHtml(this.context) + '</span></div>' : '')
      +   '<div class="cwiz-review-row">'
      +     '<span class="cwiz-review-label">Platforms</span>'
      +     '<span class="cwiz-review-value cwiz-review-plats">' + platList + '</span>'
      +   '</div>'
      +   '<div class="cwiz-review-row">'
      +     '<span class="cwiz-review-label">Schedule</span>'
      +     '<span class="cwiz-review-value">' + daysList + ' at ' + this.formatTime(this.postingTime) + '</span>'
      +   '</div>'
      +   '<div class="cwiz-review-row">'
      +     '<span class="cwiz-review-label">Duration</span>'
      +     '<span class="cwiz-review-value">' + this.durationDays + ' days' + (this.startDate ? ' starting ' + this.startDate : ' starting today') + '</span>'
      +   '</div>'
      +   '<div class="cwiz-review-row">'
      +     '<span class="cwiz-review-label">Tone</span>'
      +     '<span class="cwiz-review-value">' + (this.tone === 'brand' ? 'Brand Voice (' + this.escapeHtml(this.brainData?.profile?.voice || 'Professional') + ')' : this.tone === 'Other' ? this.escapeHtml(this.customTone || 'Professional') : this.tone) + '</span>'
      +   '</div>'
      +   '<div class="cwiz-review-row cwiz-review-highlight">'
      +     '<span class="cwiz-review-label">Total posts</span>'
      +     '<span class="cwiz-review-value"><strong>' + displayCount + '</strong> posts will be generated</span>'
      +   '</div>'
      +   (this.brainData && this.brainData.connected
          ? '<div class="cwiz-review-row cwiz-review-brain">'
          +   '<span class="cwiz-review-label">Brain</span>'
          +   '<span class="cwiz-review-value"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success,#22c55e)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:4px;"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="M22 4 12 14.01l-3-3"/></svg>Personalized with your brand voice, '
          +     (this.brainData.counts.facts || 0) + ' saved note' + ((this.brainData.counts.facts || 0) !== 1 ? 's' : '') + ' and '
          +     (this.brainData.counts.documents || 0) + ' document' + ((this.brainData.counts.documents || 0) !== 1 ? 's' : '')
          +     (this.brainData.counts.highPerformers > 0 ? ', plus ' + this.brainData.counts.highPerformers + ' of your best-performing posts' : '')
          +   '</span>'
          + '</div>'
          : '')
      + '</div>'
      + (this.generationError ? '<div class="cwiz-error">' + this.escapeHtml(this.generationError) + '</div>' : '')
      + (this.generating
          ? '<div class="cwiz-subtext" style="text-align:center;margin-top:8px;">Writing ' + estimated + ' posts one by one \u2014 this can take a minute or two. Keep this window open; we\u2019ll show the summary when it\u2019s done.</div>'
          : '')
      + '<div class="cwiz-footer">'
      +   '<button class="cwiz-btn-ghost" data-cwiz="back" ' + (this.generating ? 'disabled' : '') + '>\u2190 Back</button>'
      +   '<button class="cwiz-btn-primary" data-cwiz="launch" data-testid="campaign-create-submit" ' + (this.generating ? 'disabled' : '') + '>'
      +     (this.generating
            ? '<span class="cwiz-spinner"></span> Generating...'
            : '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:4px;"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>Launch Campaign')
      +   '</button>'
      + '</div>'
      + '</div>';
  }

  // ── Step 4: Success ───────────────────────────────────────────────────────

  renderStep4() {
    var itemCount = this.campaignResult ? this.campaignResult.itemCount : 0;
    var placeholders = this.campaignResult ? (this.campaignResult.placeholderCount || 0) : 0;

    return '<div class="cwiz-step-content cwiz-success">'
      + '<div class="cwiz-success-icon"><svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="var(--success,#22c55e)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><path d="M22 4 12 14.01l-3-3"/></svg></div>'
      + '<h2 class="cwiz-heading">Campaign launched!</h2>'
      + '<p class="cwiz-subtext"><strong>' + itemCount + ' posts</strong> are written and on your calendar for <strong>' + this.escapeHtml(this.name) + '</strong>. Posts only publish to accounts you\u2019ve connected \u2014 review or edit any of them before they go out.</p>'
      + (placeholders > 0
          ? '<div class="cwiz-error" style="text-align:left"><strong>' + placeholders + ' of these posts couldn\u2019t be written</strong> because AI generation wasn\u2019t available (no AI key or credits). They\u2019re saved with placeholder text and won\u2019t publish \u2014 add an AI key in <strong>Settings \u2192 AI</strong>, then edit or regenerate them from the campaign page.</div>'
          : '')
      + '<div class="cwiz-footer" style="justify-content:center">'
      +   '<button class="cwiz-btn-primary" data-cwiz="view-campaign" data-testid="campaign-wizard-view">View Campaign \u2192</button>'
      +   '<button class="cwiz-btn-ghost" data-cwiz="close">Close</button>'
      + '</div>'
      + '</div>';
  }

  // ── Events ────────────────────────────────────────────────────────────────

  bindEvents() {
    if (!this.overlay) return;
    var self = this;

    this.overlay.onclick = function(e) {
      if (e.target === self.overlay) { self.close(); return; }

      var btn = e.target.closest('[data-cwiz]');
      if (btn) {
        var action = btn.dataset.cwiz;
        if (action === 'close') self.close();
        if (action === 'next') self.handleNext();
        if (action === 'back') self.handleBack();
        if (action === 'launch') self.handleLaunch();
        if (action === 'view-campaign') {
          // Store campaign ID so detail view auto-opens on mount
          if (self.campaignResult && self.campaignResult.id) {
            localStorage.setItem('campaigns:lastDetailId', String(self.campaignResult.id));
          }
          self.close();
          // Force re-mount by navigating away then back
          window.location.hash = '#home';
          setTimeout(function() { window.location.hash = '#campaigns'; }, 50);
        }
      }

      // Step dot navigation
      var stepDot = e.target.closest('[data-cwiz-step]');
      if (stepDot) {
        var targetStep = parseInt(stepDot.dataset.cwizStep);
        if (targetStep < self.step && !self.generating) {
          self.step = targetStep;
          self.render();
          return;
        }
      }

      // Platform chip toggle
      var chip = e.target.closest('[data-cwiz-platform]');
      if (chip) {
        var pid = chip.dataset.cwizPlatform;
        var idx = self.selectedPlatforms.indexOf(pid);
        if (idx !== -1) self.selectedPlatforms.splice(idx, 1);
        else self.selectedPlatforms.push(pid);
        self.render();
      }

      // Day toggle
      var dayBtn = e.target.closest('[data-cwiz-day]');
      if (dayBtn) {
        var day = dayBtn.dataset.cwizDay;
        var dayIdx = self.selectedDays.indexOf(day);
        if (dayIdx !== -1) self.selectedDays.splice(dayIdx, 1);
        else self.selectedDays.push(day);
        self.render();
      }

      // Duration buttons
      var dur = e.target.closest('[data-cwiz-dur]');
      if (dur) {
        self.durationDays = parseInt(dur.dataset.cwizDur);
        self.render();
      }

      // AI media checkboxes
      var aiCheck = e.target.closest('[data-cwiz-ai]');
      if (aiCheck) {
        if (aiCheck.dataset.cwizAi === 'faceless') self.generateFacelessVideo = aiCheck.checked;
        self.render();
      }
    };

    this.overlay.oninput = function(e) {
      if (e.target.id === 'cwiz-name') {
        self.name = e.target.value;
        var nextBtn = self.overlay.querySelector('[data-cwiz="next"]');
        if (nextBtn) nextBtn.disabled = !self.name.trim() || !self.goal.trim();
      }
      if (e.target.id === 'cwiz-goal') {
        self.goal = e.target.value;
        var nextBtn2 = self.overlay.querySelector('[data-cwiz="next"]');
        if (nextBtn2) nextBtn2.disabled = !self.name.trim() || !self.goal.trim();
      }
      if (e.target.id === 'cwiz-context') {
        self.context = e.target.value;
      }
      if (e.target.id === 'cwiz-tone-custom') {
        self.customTone = e.target.value;
      }
    };

    this.overlay.onchange = function(e) {
      if (e.target.id === 'cwiz-time') {
        self.postingTime = e.target.value;
      }
      if (e.target.id === 'cwiz-tone') {
        var prev = self.tone;
        self.tone = e.target.value;
        if (prev === 'Other' || self.tone === 'Other') self.render();
      }
      if (e.target.id === 'cwiz-start-date') {
        self.startDate = e.target.value;
      }
    };
  }

  handleNext() {
    if (this.step === 1 && this.name.trim() && this.goal.trim()) {
      this.step = 2;
      this.render();
    } else if (this.step === 2 && this.selectedPlatforms.length > 0 && this.selectedDays.length > 0) {
      this.step = 3;
      this.render();
    }
  }

  handleBack() {
    if (this.step > 1 && !this.generating) {
      this.step = this.step - 1;
      this.render();
    }
  }

  async handleLaunch() {
    this.generating = true;
    this.generationError = null;
    this.render();

    // Build cadence rules
    var cadenceRules = [];
    for (var d = 0; d < this.selectedDays.length; d++) {
      for (var p = 0; p < this.selectedPlatforms.length; p++) {
        cadenceRules.push({
          day: this.selectedDays[d],
          platform: this.selectedPlatforms[p],
          time: this.postingTime
        });
      }
    }

    var estimated = this.estimatedPosts;
    var totalItems = estimated > 50 ? 50 : estimated;

    var effectiveTone = this.tone === 'brand' ? (this.brainData?.profile?.voice || 'Professional') : this.tone === 'Other' ? (this.customTone.trim() || 'Professional') : this.tone;

    var payload = {
      name: this.name,
      goal: this.goal,
      context: this.context || '',
      platforms: this.selectedPlatforms,
      cadence: {
        days: this.durationDays,
        totalItems: totalItems
      },
      cadenceRules: cadenceRules,
      tone: effectiveTone,
      startDate: this.startDate || undefined,
      include_video: this.generateFacelessVideo
    };

    try {
      var result = await client.createCampaign(payload);

      if (result.warning) {
        this.generationError = result.warning;
        this.generating = false;
        this.render();
        return;
      }

      this.campaignResult = {
        id: result.campaignId || (result.campaign ? result.campaign.id : (result.id || '')),
        itemCount: result.itemCount || (result.campaign ? result.campaign.itemCount : totalItems),
        placeholderCount: result.placeholderCount || 0
      };
      this.generating = false;
      this.step = 4;
      this.render();
      // Callers pass onComplete to refresh their own view (campaigns list /
      // create-social linkage). It was stored but never invoked — the pages
      // only "worked" via the close-time hash-bounce side effect.
      if (typeof this.onComplete === 'function') {
        try { this.onComplete(this.campaignResult.id); } catch (e) { /* non-fatal */ }
      }
    } catch (err) {
      this.generationError = err.message || 'Failed to create campaign. Please try again.';
      this.generating = false;
      this.render();
    }
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  buildToneOptions() {
    var brandVoice = this.brainData?.profile?.voice || '';
    var html = '<option value="brand"' + (this.tone === 'brand' ? ' selected' : '') + '>Brand Voice' + (brandVoice ? ' (' + brandVoice.substring(0, 20) + ')' : '') + '</option>';
    for (var i = 0; i < TONES.length; i++) {
      html += '<option value="' + TONES[i] + '"' + (this.tone === TONES[i] ? ' selected' : '') + '>' + TONES[i] + '</option>';
    }
    return html;
  }

  formatTime(timeStr) {
    if (!timeStr) return '';
    var parts = timeStr.split(':');
    var h = parseInt(parts[0]);
    var m = parts[1];
    var ampm = h >= 12 ? 'PM' : 'AM';
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return h + ':' + m + ' ' + ampm;
  }

  escapeHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  escapeAttr(str) {
    return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }
}

// Singleton launcher
var _wizardInstance = null;

export function openCampaignWizard(opts) {
  if (_wizardInstance) {
    _wizardInstance.close();
  }
  _wizardInstance = new CampaignWizard();
  _wizardInstance.open(opts);
}
